import { db, nowIso, type FoodRow } from '@/lib/db';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { upsertCatalogFood } from '@/lib/useCatalog';
import { parseOffProduct } from './parse';

export type SearchResult =
  | { status: 'ok'; foods: FoodRow[] }
  | { status: 'sin_servidor' }
  | { status: 'error'; message: string };

/**
 * Búsqueda por texto en Open Food Facts (SPEC 6.B).
 *
 * Es el plan B del escáner: sirve cuando no tienes el envase delante o el
 * código no se lee. Va SIEMPRE después de la búsqueda local, que funciona sin
 * conexión y no gasta la cuota compartida de OFF.
 *
 * Los resultados NO se guardan: son candidatos. Se guarda el que se elige
 * (`keepOffFood`), que es el único que va a usarse de verdad.
 */
export async function searchOff(query: string): Promise<SearchResult> {
  if (!isSupabaseConfigured || !supabase) return { status: 'sin_servidor' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { status: 'error', message: 'Sin conexión' };
  }

  try {
    const invoked = await supabase.functions.invoke<{ products?: unknown[]; error?: string }>(
      'off-proxy',
      { body: { query } },
    );

    if (invoked.error) {
      const message =
        invoked.error instanceof Error
          ? invoked.error.message
          : 'no se pudo buscar en Open Food Facts';
      return { status: 'error', message };
    }

    const products = invoked.data?.products ?? [];
    const foods: FoodRow[] = [];

    for (const product of products) {
      const code = (product as { code?: unknown }).code;
      if (typeof code !== 'string' || code.length === 0) continue;

      // La respuesta de búsqueda trae los productos «desnudos», sin el sobre
      // de v3: se envuelve para reutilizar el MISMO parseo y no tener dos
      // sitios que sepan del formato de OFF (DECISIONS.md D-08).
      const parsed = parseOffProduct({ status: 'success', code, product }, code);
      if (parsed.found) {
        foods.push({ ...parsed.food, updatedAt: nowIso(), deletedAt: null });
      }
    }

    return { status: 'ok', foods };
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : 'error desconocido' };
  }
}

/** Guarda en el móvil el producto elegido de la búsqueda. */
export async function keepOffFood(food: FoodRow): Promise<FoodRow> {
  const existing = await db.foods.get(food.id);
  const row: FoodRow = existing ? { ...existing, deletedAt: null } : food;
  await db.foods.put(row);
  upsertCatalogFood(row);
  return row;
}
